import { formatPostalAddress } from "@somasi/shared";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Building2, Pencil, Receipt, Users } from "lucide-react";

import { api } from "@/integrations/aws/resources";
import type { Company } from "@somasi/shared";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import {
  ListPageTable,
  ListSearch,
  TablePagination,
  useDebounced,
  usePagination,
} from "@/components/list-controls";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClientGroupDialog, ExportMenu, type ClientGroup } from "./client-groups";
import { STATUS_LABEL, STATUS_TONE } from "@/lib/companies";
import {
  GROUP_COMPANY_PDF_COLUMNS,
  TYPE_LABEL,
  groupCompanyExportRow,
  groupFilenameBase,
} from "@/lib/client-groups";

/**
 * One client group: what it is, the companies held under it, and the people
 * attached to the group itself.
 *
 * The route is `client-groups_` rather than `client-groups` so it is a sibling
 * of the list page rather than a child of it. A child would render inside
 * `client-groups.tsx`, which draws a full-page table and no `<Outlet />`, so
 * nothing of the child would appear — which is exactly what used to happen to
 * `/client-groups/$groupId/pricing`. The URL is unaffected; only the nesting is.
 */
export const Route = createFileRoute("/_authenticated/client-groups_/$groupId")({
  head: () => ({
    meta: [
      { title: "Client Group — Somasi Ops" },
      {
        name: "description",
        content: "A client group with the companies held under it and its key contacts.",
      },
    ],
  }),
  component: ClientGroupPage,
});

type CompanyRow = {
  id: string;
  legal_name: string;
  trading_name: string | null;
  registration_number: string | null;
  jurisdiction: string | null;
  incorporation_date: string | null;
  status: keyof typeof STATUS_LABEL;
  afr_due_date: string | null;
};

type ContactRow = {
  id: string;
  full_name: string;
  job_title: string | null;
  primary_email: string | null;
  primary_phone: string | null;
  company_id: string | null;
  is_active: boolean;
};

const COMPANY_COLUMNS =
  "id, legal_name, trading_name, registration_number, jurisdiction, incorporation_date, status, afr_due_date";

function ClientGroupPage() {
  const { groupId } = Route.useParams();
  const { canEdit } = useAuth();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("companies");

  const query = useDebounced(search.trim());
  const pager = usePagination({ resetKey: [groupId, query] });

  const { data: group, isLoading: groupLoading } = useQuery({
    queryKey: ["client_group", groupId],
    queryFn: async () => {
      const { data, error } = await api.clientGroups.get(groupId);
      if (error) throw error;
      return data as ClientGroup & {
        relationship_manager: string | null;
        fee_schedule_id: string | null;
      };
    },
  });

  /**
   * The companies under this group, paged and searched at the API.
   *
   * `client_group_id` is a declared parent, so the filter is a Query on
   * `by_client_group_id` rather than a scan — which is what makes this bearable
   * for Astride US LLC, whose 655 companies would otherwise be a full table read
   * to render twenty rows.
   */
  const companyList = () => {
    const list = api.companies.list().eq("client_group_id", groupId).order("legal_name");
    if (query) {
      list.or([
        { column: "legal_name", op: "ilike", value: `%${query}%` },
        { column: "trading_name", op: "ilike", value: `%${query}%` },
        { column: "registration_number", op: "ilike", value: `%${query}%` },
      ]);
    }
    return list;
  };

  const { data: companies, isLoading: companiesLoading } = useQuery({
    queryKey: [
      "client_group_companies",
      groupId,
      { query, page: pager.page, size: pager.pageSize },
    ],
    queryFn: async () => {
      const { data, error, count } = await companyList()
        .select(COMPANY_COLUMNS)
        .page(pager.page, pager.pageSize);
      if (error) throw error;
      return { rows: (data ?? []) as CompanyRow[], total: count ?? 0 };
    },
    placeholderData: (previous) => previous,
  });

  /**
   * The contacts attached to the group itself.
   *
   * Every contact in the group sits in the one `by_client_group_id` partition,
   * whether it hangs off the group or off a company inside it — so the ones
   * belonging to the group as a whole are those with no `company_id`, which is
   * how the model holds "a contact of this client, not of one of its companies".
   * The narrowing is sent as a filter rather than done here, so a group with
   * hundreds of company contacts does not download them to discard them.
   */
  const { data: contacts = [], isLoading: contactsLoading } = useQuery({
    queryKey: ["client_group_contacts", groupId],
    queryFn: async () => {
      const { data, error } = await api.contacts
        .list()
        .all()
        .select("id, full_name, job_title, primary_email, primary_phone, company_id, is_active")
        .eq("client_group_id", groupId)
        .is("company_id", null)
        .order("full_name");
      if (error) throw error;
      return (data ?? []) as ContactRow[];
    },
  });

  /**
   * Every company in the group, whatever the table is showing.
   *
   * The table is paged and searched, so what is on screen is twenty rows of
   * however many the group has; this is the whole list, which is what someone
   * asking for "the companies under this client" means. The search is
   * deliberately not applied — the page's search is for finding one company,
   * not for narrowing a download.
   */
  const companyExportRows = async () => {
    const { data, error } = await api.companies
      .list()
      .eq("client_group_id", groupId)
      .order("legal_name")
      .all();
    if (error) throw new Error(error.message);
    return ((data ?? []) as Company[]).map(groupCompanyExportRow);
  };

  const { data: schedule } = useQuery({
    queryKey: ["fee_schedule_name", group?.fee_schedule_id],
    enabled: !!group?.fee_schedule_id,
    queryFn: async () => {
      const { data, error } = await api.feeSchedules.get(group!.fee_schedule_id!, ["id", "name"]);
      if (error) throw error;
      return data as { id: string; name: string };
    },
  });

  if (groupLoading) return <div className="p-8 text-muted-foreground">Loading…</div>;

  if (!group) {
    return (
      <Alert variant="destructive" className="m-8 max-w-xl">
        <AlertTitle>Client group not found</AlertTitle>
        <AlertDescription>
          It may have been deleted. Go back to the{" "}
          <Link to="/client-groups" className="underline">
            client groups list
          </Link>
          .
        </AlertDescription>
      </Alert>
    );
  }

  const total = companies?.total;
  const rows = companies?.rows ?? [];
  // Empty when the group has no address, which is what hides the card below.
  const addressLines = formatPostalAddress(group, group.country);

  return (
    // Fills the window like a list page: the facts stay on top, and whichever
    // table is open fills the rest and scrolls inside its own card.
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        title={group.name}
        description={
          typeof total === "number"
            ? `${TYPE_LABEL[group.client_type]} · ${total} ${total === 1 ? "company" : "companies"}`
            : TYPE_LABEL[group.client_type]
        }
        breadcrumbs={[{ label: "Client Groups", to: "/client-groups" }, { label: group.name }]}
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/client-groups">
                <ArrowLeft className="mr-2 h-4 w-4" /> Client groups
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/client-groups/$groupId/pricing" params={{ groupId }}>
                <Receipt className="mr-2 h-4 w-4" /> Group pricing
              </Link>
            </Button>
            {canEdit && (
              <Button onClick={() => setEditOpen(true)}>
                <Pencil className="mr-2 h-4 w-4" /> Edit group
              </Button>
            )}
          </>
        }
      />

      <div className="mb-4 grid shrink-0 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Fact label="Type" value={TYPE_LABEL[group.client_type]} />
        <Fact label="Country" value={group.country ?? "—"} />
        <Fact label="Payment terms" value={group.default_payment_terms ?? "—"} />
        <Fact label="Relationship manager" value={group.relationship_manager ?? "—"} />
        <Fact label="Status" value={group.is_active ? "Active" : "Inactive"} />
        <Fact label="Companies" value={typeof total === "number" ? String(total) : "…"} />
        <Fact label="Group contacts" value={contactsLoading ? "…" : String(contacts.length)} />
        <Fact
          label="Fee schedule"
          value={
            group.fee_schedule_id
              ? (schedule?.name ?? "…")
              : "None — companies bill at the standard fee"
          }
        />
      </div>

      {addressLines.length > 0 && (
        <div className="mb-4 shrink-0 rounded-md border bg-card px-3 py-2">
          <div className="text-xs text-muted-foreground">Billing address</div>
          <address className="mt-1 text-sm font-medium not-italic leading-relaxed">
            {addressLines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </address>
        </div>
      )}

      {group.notes && (
        <Alert className="mb-4 shrink-0">
          <AlertTitle>Notes</AlertTitle>
          <AlertDescription className="whitespace-pre-wrap">{group.notes}</AlertDescription>
        </Alert>
      )}

      {/* The two lists as tabs, with the companies' search and export beside
          them, so the open one can take the rest of the window. */}
      <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <TabsList className="shrink-0">
            <TabsTrigger value="companies" className="gap-2">
              <Building2 className="h-3.5 w-3.5" /> Companies
              {typeof total === "number" && (
                <span className="text-xs tabular-nums opacity-70">{total}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="contacts" className="gap-2">
              <Users className="h-3.5 w-3.5" /> Group contacts
              {!contactsLoading && (
                <span className="text-xs tabular-nums opacity-70">{contacts.length}</span>
              )}
            </TabsTrigger>
          </TabsList>
          {tab === "companies" ? (
            <>
              <ListSearch
                value={search}
                onChange={setSearch}
                placeholder="Search by name or registration number…"
              />
              <ExportMenu
                filenameBase={groupFilenameBase(group.name, "companies")}
                rows={companyExportRows}
                pdfCols={GROUP_COMPANY_PDF_COLUMNS}
                title={`${group.name} — Companies`}
              />
            </>
          ) : (
            <p className="text-muted-foreground text-xs">
              People attached to the group itself. A company's own contacts are on that company.
            </p>
          )}
        </div>

        <TabsContent
          value="companies"
          className="mt-0 hidden min-h-0 flex-1 flex-col data-[state=active]:flex"
        >
          <ListPageTable>
            <Table containerClassName="min-h-0 flex-1">
              <TableHeader sticky>
                <TableRow>
                  <TableHead>Legal name</TableHead>
                  <TableHead className="w-32">Registration number</TableHead>
                  <TableHead className="w-44">Jurisdiction</TableHead>
                  <TableHead className="w-32">Incorporated</TableHead>
                  <TableHead className="w-32">Status</TableHead>
                  <TableHead className="w-32">AFR due</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companiesLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      {query
                        ? "No company here matches that."
                        : "No companies are attached to this group yet."}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <Link
                          to="/companies/$companyId/services"
                          params={{ companyId: c.id }}
                          className="font-medium hover:underline"
                        >
                          {c.legal_name}
                        </Link>
                        {c.trading_name && (
                          <div className="text-xs text-muted-foreground">t/a {c.trading_name}</div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {c.registration_number ?? "—"}
                      </TableCell>
                      <TableCell>{c.jurisdiction ?? "—"}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(c.incorporation_date)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_TONE[c.status]} hover:opacity-90`}>
                          {STATUS_LABEL[c.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(c.afr_due_date)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              pagination={pager}
              total={total}
              loading={companiesLoading}
              unit="company"
              units="companies"
            />
          </ListPageTable>
        </TabsContent>

        <TabsContent
          value="contacts"
          className="mt-0 hidden min-h-0 flex-1 flex-col data-[state=active]:flex"
        >
          <ListPageTable>
            <Table containerClassName="min-h-0 flex-1">
              <TableHeader sticky>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-56">Job title</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="w-44">Phone</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contactsLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : contacts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                      No contacts are attached to the group itself.
                    </TableCell>
                  </TableRow>
                ) : (
                  contacts.map((k) => (
                    <TableRow key={k.id}>
                      <TableCell className="font-medium">{k.full_name}</TableCell>
                      <TableCell>{k.job_title ?? "—"}</TableCell>
                      <TableCell>
                        {k.primary_email ? (
                          <a href={`mailto:${k.primary_email}`} className="hover:underline">
                            {k.primary_email}
                          </a>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{k.primary_phone ?? "—"}</TableCell>
                      <TableCell>
                        {k.is_active ? (
                          <Badge className="bg-success text-success-foreground hover:bg-success/90">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline">Inactive</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ListPageTable>
        </TabsContent>
      </Tabs>

      <ClientGroupDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        editing={group}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ["client_group", groupId] });
          qc.invalidateQueries({ queryKey: ["client_groups"] });
        }}
      />
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-card px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="truncate text-sm font-medium" title={value}>
        {value}
      </div>
    </div>
  );
}
